/**
 * Session Monitoring Hook
 * 
 * Feature: 033-auth-complete (Phase 4 - User Story 2)
 * Description: Tracks user activity and enforces idle timeout
 * Constitution: v1.11.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import axios from 'axios';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_UPDATE_INTERVAL_MS = 60 * 1000; // 1 minute
const WARNING_BEFORE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes warning

interface UseSessionMonitorOptions {
  onIdleWarning?: (remainingSeconds: number) => void;
  onIdleTimeout?: () => void;
}

export const useSessionMonitor = (options: UseSessionMonitorOptions = {}) => {
  const { isAuthenticated, logout } = useAuthStore();
  const lastActivityRef = useRef<number>(Date.now());
  const activityUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef<boolean>(false);

  /**
   * Update last activity timestamp
   */
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  /**
   * Send activity ping to backend
   */
  const sendActivityPing = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      await axios.post('/api/auth/session/activity');
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  }, [isAuthenticated]);

  /**
   * Check for idle timeout
   */
  const checkIdleTimeout = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    const remainingTime = IDLE_TIMEOUT_MS - timeSinceLastActivity;

    // Show warning 2 minutes before timeout
    if (remainingTime <= WARNING_BEFORE_TIMEOUT_MS && !warningShownRef.current) {
      warningShownRef.current = true;
      const remainingSeconds = Math.floor(remainingTime / 1000);
      options.onIdleWarning?.(remainingSeconds);
    }

    // Timeout reached
    if (timeSinceLastActivity >= IDLE_TIMEOUT_MS) {
      console.log('Session idle timeout reached');
      options.onIdleTimeout?.();
      logout();
    }
  }, [logout, options]);

  /**
   * Setup activity listeners
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Send activity ping every minute
    activityUpdateIntervalRef.current = setInterval(() => {
      sendActivityPing();
    }, ACTIVITY_UPDATE_INTERVAL_MS);

    // Check for idle timeout every 10 seconds
    idleCheckIntervalRef.current = setInterval(() => {
      checkIdleTimeout();
    }, 10000);

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });

      if (activityUpdateIntervalRef.current) {
        clearInterval(activityUpdateIntervalRef.current);
      }

      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [isAuthenticated, updateActivity, sendActivityPing, checkIdleTimeout]);

  return {
    updateActivity,
    lastActivity: lastActivityRef.current,
  };
};
